import { useQuery } from '@tanstack/react-query';
import { apiRoutes } from '../../../../constants/apiRoutes';
import { getAxios } from '../../../../util/axios';
import type { QuickLinkCategoryModel, QuickLinkModel } from '../types/QuickLinkModels';

export const quickLinksNavigationQueryKey = ['quickLinksNavigation'];
export const quickLinksQueryKey = ['quickLinks'];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isQuickLinkModel(value: unknown): value is QuickLinkModel {
  return (
    isRecord(value) &&
    typeof value.Id === 'number' &&
    typeof value.DisplayName === 'string' &&
    typeof value.Category === 'string' &&
    typeof value.QuickLinkType === 'number' &&
    typeof value.ShowInNav === 'boolean' &&
    (typeof value.FileName === 'string' || value.FileName === null || value.FileName === undefined) &&
    (typeof value.Url === 'string' || value.Url === null || value.Url === undefined) &&
    (typeof value.ResolvedUrl === 'string' || value.ResolvedUrl === null || value.ResolvedUrl === undefined)
  );
}

function isQuickLinkCategoryModel(value: unknown): value is QuickLinkCategoryModel {
  return (
    isRecord(value) &&
    typeof value.Category === 'string' &&
    Array.isArray(value.Links) &&
    value.Links.every(isQuickLinkModel)
  );
}

function isQuickLinkCategoryModelArray(value: unknown): value is QuickLinkCategoryModel[] {
  return Array.isArray(value) && value.every(isQuickLinkCategoryModel);
}

export async function fetchQuickLinks(showInNavOnly = false) {
  const response = await getAxios().get<unknown>(apiRoutes.quickLinks.list(showInNavOnly));

  if (!isQuickLinkCategoryModelArray(response.data)) {
    throw new Error('Quick Links response was not a valid list.');
  }

  return response.data;
}

export async function fetchQuickLinksNavigation() {
  return await fetchQuickLinks(true);
}

export function useQuickLinksQuery() {
  return useQuery({
    queryKey: quickLinksQueryKey,
    queryFn: () => fetchQuickLinks(false),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false
  });
}

export function useQuickLinksNavigationQuery() {
  return useQuery({
    queryKey: quickLinksNavigationQueryKey,
    queryFn: fetchQuickLinksNavigation,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false
  });
}
